/**
 * ChannelsPanel · Setup > Direct sales channels (Feb 2026 · Iter 205)
 * ─────────────────────────────────────────────────────────────────
 * Sellers who ALSO run marketplace / DTC storefronts (Amazon FBM,
 * TikTok Shop, Walmart, Shopify DTC, Etsy, eBay) can wire those
 * accounts here. Orders from connected channels will flow into the
 * SAME Fulfilment > Orders queue as wholesale buyer orders — one
 * unified fulfilment surface across all revenue streams.
 *
 * Phase 1 (this file): CRUD + mocked OAuth handshake. Real OAuth for
 * each provider (Amazon SP-API, TikTok Shop Partner, Walmart MP API,
 * Shopify Partner) is queued for Phase 2 — the backend endpoints
 * stay stable so no frontend rewiring will be needed.
 */
import { useEffect, useState } from "react";
import api from "../../../../lib/api";
import { toast } from "sonner";
import {
  Zap, ExternalLink, Loader2, Plus, Trash2, Pause, Play,
  Check, AlertTriangle, X, Copy, RefreshCw, Key, Clock, Bell, Upload,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import ChannelIcon from "./ChannelIcon";
import WholesaleChannelDrawer from "./WholesaleChannelDrawer";

// Iter 210 · Which channels are wired to REAL provider OAuth.
// Everything else renders as "Coming soon" — connect CTA disabled +
// notify-me action so we still capture demand signal.
const REAL_OAUTH_CHANNELS = new Set(["shopify_dtc", "generic_api", "generic_wholesale"]);
// Iter 210 · Channels that ARE functional but still carry a "Coming
// soon" visual pill to signal beta / evolving surface. Connect flow
// stays enabled — the pill is informational, not blocking.
const PREVIEW_LABEL_CHANNELS = new Set(["generic_api"]);
const COMING_SOON_LABELS = {
  amazon:  "Amazon SP-API partner approval in progress",
  tiktok:  "TikTok Shop Partner review in progress",
  walmart: "Walmart Marketplace credential flow · manual key paste",
  etsy:    "Etsy OAuth partner review",
  ebay:    "eBay Trading API partner review",
  generic_api: "Beta · public API surface still evolving",
};

/** Provider-specific accents · matches the "brand-y" pill on cards
 *  without pulling every real marketplace logo. Keeps the setup tab
 *  visually cohesive with the rest of the app. */
const CHANNEL_ACCENTS = {
  amazon:      { bg: "bg-amber-50",  fg: "text-amber-800",  border: "border-amber-200"  },
  tiktok:      { bg: "bg-rose-50",   fg: "text-rose-800",   border: "border-rose-200"   },
  walmart:     { bg: "bg-sky-50",    fg: "text-sky-800",    border: "border-sky-200"    },
  shopify_dtc: { bg: "bg-emerald-50",fg: "text-emerald-800",border: "border-emerald-200"},
  etsy:        { bg: "bg-orange-50", fg: "text-orange-800", border: "border-orange-200" },
  ebay:        { bg: "bg-indigo-50", fg: "text-indigo-800", border: "border-indigo-200" },
  generic_api: { bg: "bg-violet-50", fg: "text-violet-800", border: "border-violet-200" },
  generic_wholesale:{ bg: "bg-slate-100",fg: "text-slate-800",border: "border-slate-300"},
};

export default function ChannelsPanel() {
  const [types, setTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectDialog, setConnectDialog] = useState(null);   // channel-type object or null
  const [connectBusy, setConnectBusy] = useState(false);
  const [storeName, setStoreName] = useState("");
  // Iter 210 · one-time reveal of the API key for a `generic_api` channel.
  const [apiKeyReveal, setApiKeyReveal] = useState(null); // {key, channelId, storeName}
  // Iter 210 · Wholesale channel management drawer (CSV / email / scheduled).
  const [wholesaleDrawer, setWholesaleDrawer] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/dropship/channels/types").then(r => setTypes(r.data || [])),
      api.get("/dropship/channels").then(r => setChannels(r.data || [])),
    ])
      .catch(() => toast.error("Could not load channels"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submitConnect = async () => {
    if (!connectDialog || !storeName.trim()) return;
    setConnectBusy(true);
    try {
      const r = await api.post("/dropship/channels", {
        type: connectDialog.id,
        store_name: storeName.trim(),
      });
      toast.success(`${connectDialog.label} connected`);
      const created = r.data || {};
      setConnectDialog(null);
      setStoreName("");
      // For Custom API channels, the server returns the raw key ONCE.
      // Surface a modal so the seller can copy it before it's gone.
      if (created.api_key) {
        setApiKeyReveal({
          key: created.api_key,
          channelId: created.id,
          storeName: created.store_name,
        });
      }
      // Iter 210 · For Wholesale (manual/CSV) channels, auto-open the
      // management drawer so the seller lands on the CSV upload flow.
      if (created.type === "generic_wholesale") {
        setWholesaleDrawer(created);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect");
    } finally {
      setConnectBusy(false);
    }
  };

  const disconnect = async (ch) => {
    if (!window.confirm(`Disconnect ${ch.store_name}? Historical orders remain intact.`)) return;
    try {
      await api.delete(`/dropship/channels/${ch.id}`);
      toast.success("Channel disconnected");
      load();
    } catch { toast.error("Could not disconnect"); }
  };

  const togglePause = async (ch) => {
    const next = ch.status === "paused" ? "active" : "paused";
    try {
      await api.patch(`/dropship/channels/${ch.id}`, { status: next });
      toast.success(next === "paused" ? "Channel paused" : "Channel resumed");
      load();
    } catch { toast.error("Could not update"); }
  };

  const testConnection = async (ch) => {
    try {
      const r = await api.post(`/dropship/channels/${ch.id}/test`);
      toast.success(`OK · ${r.data?.latency_ms || "?"}ms`);
    } catch { toast.error("Health check failed"); }
  };

  const rotateApiKey = async (ch) => {
    if (!window.confirm(
      `Rotate API key for "${ch.store_name}"?\n\nThe current key stops working IMMEDIATELY. ` +
      `Update any integrations that push orders using it.`
    )) return;
    try {
      const r = await api.post(`/dropship/channels/${ch.id}/rotate-key`);
      if (r.data?.api_key) {
        setApiKeyReveal({ key: r.data.api_key, channelId: ch.id, storeName: ch.store_name, rotated: true });
      }
    } catch { toast.error("Could not rotate key"); }
  };

  // Iter 210 · Real Shopify OAuth install kickoff.
  // Prompts the seller for their .myshopify.com domain then redirects the
  // browser to the persona-aware install endpoint which builds the real
  // Shopify authorize URL using SHOPIFY_SUPPLIER_CLIENT_ID/SECRET (env).
  // On completion Shopify redirects back to our /shopify-app/oauth/callback
  // which upserts BOTH the dropship_stores row AND a sales_channels row
  // (see routes/shopify_app.py::oauth_callback), so the card here flips
  // from "Connect" to "connected · live" on the next `load()`.
  const startShopifyOAuth = async () => {
    let shop = window.prompt(
      "Enter your Shopify store domain\n\n" +
      "Example: mybrand.myshopify.com  (or just: mybrand)"
    );
    if (!shop) return;
    shop = shop.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!shop.endsWith(".myshopify.com")) {
      shop = shop.replace(/\.myshopify\.com$/i, "") + ".myshopify.com";
    }
    setConnectBusy(true);
    try {
      const r = await api.get(`/shopify-app/install?persona=supplier&shop=${encodeURIComponent(shop)}`);
      const url = r.data?.url;
      if (!url) throw new Error("no install url");
      // Redirect the browser to Shopify's authorize screen (or the mock
      // loopback when SHOPIFY_SUPPLIER_CLIENT_ID is missing).
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message ||
                  e?.response?.data?.detail ||
                  "Could not start Shopify install");
      setConnectBusy(false);
    }
  };

  const notifyMe = (label) => {
    toast.success(`We'll ping you the moment ${label} goes live.`,
                  { icon: "🔔" });
  };

  /** Phase-2 · Category A6 · Push N mock orders into the Fulfilment
      queue tagged as sourced from this channel. Lets sellers preview
      the unified Wholesale + DTC surface right away. */
  const seedOrders = async (ch, n = 5) => {
    try {
      const r = await api.post(`/dropship/channels/${ch.id}/seed-orders?count=${n}`);
      toast.success(`Seeded ${r.data?.created || n} orders from ${ch.store_name}`);
      load();
    } catch { toast.error("Could not seed orders"); }
  };

  // Map channels by type so we can render connected ones inside their card.
  const byType = {};
  for (const ch of channels) {
    if (!byType[ch.type]) byType[ch.type] = [];
    byType[ch.type].push(ch);
  }

  return (
    <div data-testid="channels-panel">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5">Direct sales channels</div>
          <div className="text-[13.5px] font-medium">Connect your marketplace + DTC storefronts</div>
          <div className="text-[11.5px] text-foreground/55 max-w-2xl mt-0.5">
            Orders from Amazon, TikTok Shop, Walmart, and other channels you own will flow into the same Fulfillment queue as your wholesale buyer orders — one unified surface for every revenue stream.
          </div>
        </div>
        {channels.length > 0 && (
          <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/50 pt-1.5">
            {channels.length} connected · {channels.filter(c => c.status === "active").length} active
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center text-foreground/40">
          <Loader2 className="w-5 h-5 animate-spin"/>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="channel-type-grid">
          {types.map(t => {
            const accent = CHANNEL_ACCENTS[t.id] || { bg: "bg-secondary", fg: "text-foreground", border: "border-border" };
            const connected = byType[t.id] || [];
            const comingSoon = !REAL_OAUTH_CHANNELS.has(t.id);
            // Iter 210 · show a "Coming soon" pill on functional-but-beta
            // channels too (custom API), without disabling the connect flow.
            const showComingSoonPill = comingSoon || PREVIEW_LABEL_CHANNELS.has(t.id);
            return (
              <div key={t.id}
                data-testid={`channel-card-${t.id}`}
                className={`relative rounded-2xl border ${accent.border} bg-white p-4 flex flex-col gap-3 ${comingSoon ? "opacity-95" : ""}`}
              >
                {/* Iter 210 · Coming-soon ribbon. Marketplace channels
                    (Amazon, TikTok, Walmart, Etsy, eBay) are fully
                    disabled awaiting partner approval. Custom API is
                    functional but tagged beta while the API surface
                    stabilizes. */}
                {showComingSoonPill && (
                  <div
                    data-testid={`channel-coming-soon-${t.id}`}
                    className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-violet-600 text-white shadow-sm"
                  >
                    <Clock className="w-2.5 h-2.5"/> Coming soon
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg ${accent.bg} ${accent.fg} grid place-items-center shrink-0`}>
                    <ChannelIcon type={t.id} className="w-4 h-4"/>
                  </div>
                  <div className="min-w-0 flex-1 pr-16">
                    <div className="text-[13px] font-semibold truncate">{t.label}</div>
                    <div className="text-[11px] text-foreground/50 mt-0.5">{t.notes}</div>
                    <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/40 mt-1">
                      {t.flow === "fbm" ? "Fulfilled by Merchant" :
                       t.flow === "api" ? "Custom API integration" :
                       t.flow === "b2b_manual" ? "Manual / CSV wholesale" :
                       "Direct-to-consumer"}
                    </div>
                    {showComingSoonPill && COMING_SOON_LABELS[t.id] && (
                      <div className="text-[10.5px] text-violet-700/80 mt-1.5 italic">
                        {COMING_SOON_LABELS[t.id]}
                      </div>
                    )}
                  </div>
                </div>

                {/* Connected accounts of this type */}
                {connected.length > 0 && (
                  <div className="space-y-1.5 border-t border-border pt-2.5">
                    {connected.map(ch => (
                      <div key={ch.id}
                        data-testid={`channel-item-${ch.id}`}
                        className="flex items-center gap-2 rounded-lg bg-secondary/40 px-2.5 py-1.5"
                      >
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          ch.status === "active" ? "bg-emerald-500" :
                          ch.status === "paused" ? "bg-amber-500" : "bg-foreground/30"
                        }`}/>
                        <div className="min-w-0 flex-1 text-[11.5px] font-medium truncate">{ch.store_name}</div>
                        <button
                          onClick={() => seedOrders(ch, 5)}
                          title="Seed 5 mock orders from this channel into the Fulfilment queue"
                          data-testid={`channel-seed-${ch.id}`}
                          className="w-6 h-6 rounded hover:bg-white grid place-items-center text-emerald-700/70 hover:text-emerald-800"
                        >
                          <Plus className="w-3 h-3"/>
                        </button>
                        {ch.type === "generic_wholesale" && (
                          <button
                            onClick={() => setWholesaleDrawer(ch)}
                            title="Manage imports (CSV / email / scheduled)"
                            data-testid={`channel-manage-${ch.id}`}
                            className="w-6 h-6 rounded hover:bg-white grid place-items-center text-slate-700/70 hover:text-slate-800"
                          >
                            <Upload className="w-3 h-3"/>
                          </button>
                        )}
                        {ch.type === "generic_api" && (
                          <button
                            onClick={() => rotateApiKey(ch)}
                            title="Rotate API key (invalidates the current key)"
                            data-testid={`channel-rotate-key-${ch.id}`}
                            className="w-6 h-6 rounded hover:bg-white grid place-items-center text-violet-700/70 hover:text-violet-800"
                          >
                            <RefreshCw className="w-3 h-3"/>
                          </button>
                        )}
                        <button
                          onClick={() => testConnection(ch)}
                          title="Test connection"
                          data-testid={`channel-test-${ch.id}`}
                          className="w-6 h-6 rounded hover:bg-white grid place-items-center text-foreground/50 hover:text-foreground"
                        >
                          <Zap className="w-3 h-3"/>
                        </button>
                        <button
                          onClick={() => togglePause(ch)}
                          title={ch.status === "paused" ? "Resume" : "Pause"}
                          data-testid={`channel-pause-${ch.id}`}
                          className="w-6 h-6 rounded hover:bg-white grid place-items-center text-foreground/50 hover:text-foreground"
                        >
                          {ch.status === "paused" ? <Play className="w-3 h-3"/> : <Pause className="w-3 h-3"/>}
                        </button>
                        <button
                          onClick={() => disconnect(ch)}
                          title="Disconnect"
                          data-testid={`channel-disconnect-${ch.id}`}
                          className="w-6 h-6 rounded hover:bg-white grid place-items-center text-rose-600/70 hover:text-rose-700"
                        >
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Connect CTA · Iter 210 · three paths:
                    · shopify_dtc → real Shopify OAuth
                    · coming-soon channels → Notify me
                    · generic_api / generic_wholesale → mocked label prompt */}
                {t.id === "shopify_dtc" ? (
                  <button
                    onClick={startShopifyOAuth}
                    disabled={connectBusy}
                    data-testid={`channel-connect-${t.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-full h-8 px-3 text-[11.5px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
                  >
                    {connectBusy
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin"/>
                      : <ExternalLink className="w-3.5 h-3.5"/>}
                    {connected.length > 0 ? "Connect another store" : "Connect with Shopify"}
                  </button>
                ) : comingSoon ? (
                  <button
                    onClick={() => notifyMe(t.label)}
                    data-testid={`channel-notify-${t.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-full h-8 px-3 text-[11.5px] font-medium border border-violet-200 bg-white text-violet-700 hover:bg-violet-50"
                  >
                    <Bell className="w-3.5 h-3.5"/>Notify me when live
                  </button>
                ) : (
                  <button
                    onClick={() => { setConnectDialog(t); setStoreName(""); }}
                    data-testid={`channel-connect-${t.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-full h-8 px-3 text-[11.5px] font-medium border border-border bg-white hover:bg-secondary/60"
                  >
                    <Plus className="w-3.5 h-3.5"/>
                    {connected.length > 0 ? "Add another" : "Connect"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Phase 2 note · Iter 210 · rewritten now that Shopify is real */}
      <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2 text-[11px] flex items-start gap-2">
        <Check className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
        <div>
          <span className="font-semibold">Shopify DTC is live</span> — real OAuth,
          real orders, real fulfillment writeback via our Supplier app. Amazon /
          TikTok / Walmart / Etsy / eBay are pending their respective partner
          approvals; you&apos;ll see them flip from &quot;Coming soon&quot; to
          &quot;Connect&quot; automatically when they clear.
        </div>
      </div>

      {/* Connect dialog */}
      <Dialog open={!!connectDialog} onOpenChange={(v) => !v && setConnectDialog(null)}>
        <DialogContent className="max-w-md" data-testid="channel-connect-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {connectDialog && <ChannelIcon type={connectDialog.id} className="w-4 h-4"/>}
              Connect {connectDialog?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[12px] text-foreground/60">
              Phase 1 mocked handshake — enter a store label so you can identify this account later. Real OAuth for {connectDialog?.label} rolls out in Phase 2.
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 block mb-1">Store label</label>
              <Input
                placeholder={connectDialog?.id === "amazon" ? "Verqo Demo US" :
                             connectDialog?.id === "tiktok" ? "@verqodemo" :
                             connectDialog?.id === "walmart" ? "Walmart Seller #12345" :
                             "My storefront"}
                value={storeName}
                onChange={e => setStoreName(e.target.value)}
                data-testid="channel-store-name-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialog(null)} data-testid="channel-cancel">
              <X className="w-3.5 h-3.5 mr-1"/>Cancel
            </Button>
            <Button
              onClick={submitConnect}
              disabled={!storeName.trim() || connectBusy}
              data-testid="channel-submit"
            >
              {connectBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1"/> : <Check className="w-3.5 h-3.5 mr-1"/>}
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 209 · One-time API key reveal for Custom API channels */}
      <Dialog open={!!apiKeyReveal} onOpenChange={(v) => !v && setApiKeyReveal(null)}>
        <DialogContent className="max-w-lg" data-testid="channel-api-key-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-4 h-4 text-violet-700"/>
              {apiKeyReveal?.rotated ? "New API key" : "Your API key"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-[11.5px] flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
              <div>
                <span className="font-semibold">Copy this key now.</span> We store only a
                hash — this is the only time we&apos;ll show it. If you lose it, use the
                Rotate button on the channel to mint a new one.
              </div>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 block mb-1">
                API Key · {apiKeyReveal?.storeName}
              </label>
              <div className="flex items-center gap-2">
                <code data-testid="channel-api-key-value"
                      className="flex-1 mono text-[12px] rounded-md bg-secondary/60 px-3 py-2 truncate border border-border">
                  {apiKeyReveal?.key}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="channel-api-key-copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(apiKeyReveal?.key || "");
                      toast.success("Copied to clipboard");
                    } catch { toast.error("Copy failed — select and copy manually"); }
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1"/>Copy
                </Button>
              </div>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 block mb-1">
                Push orders to this endpoint
              </label>
              <pre data-testid="channel-api-curl"
                   className="mono text-[10.5px] rounded-md bg-foreground/[0.03] border border-border px-3 py-2 whitespace-pre-wrap break-all leading-relaxed">
{`curl -X POST \\
  ${(typeof window !== "undefined" ? window.location.origin : "")}/api/v1/dropship/channels/${apiKeyReveal?.channelId}/orders \\
  -H "Authorization: Bearer ${apiKeyReveal?.key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_order_id": "ORD-1001",
    "customer_name":  "Ava Thompson",
    "ship_to_street": "817 Sunset Blvd",
    "ship_to_city":   "Austin",
    "ship_to_state":  "TX",
    "ship_to_zip":    "78704",
    "lines": [{"sku":"V-1006","qty":2,"unit_price":15.00}]
  }'`}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setApiKeyReveal(null)}
              data-testid="channel-api-key-done"
            >
              <Check className="w-3.5 h-3.5 mr-1"/>I&apos;ve saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 210 · Wholesale channel management drawer */}
      <WholesaleChannelDrawer
        open={!!wholesaleDrawer}
        channel={wholesaleDrawer}
        onClose={() => setWholesaleDrawer(null)}
        onImport={load}
      />
    </div>
  );
}
